import { useState } from 'react';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

import Alert from '@/components/Alert';
import Card from '@/components/Card';
import FormElement from '@/components/FormElement';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import TextArea from '@/components/TextArea';
import Button from '@/components/Button';
import { Body, Small } from '@/components/Typography';
import { accountabilityAPI } from '@/services/accountabilityAPI';

import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  TextField,
} from '@mui/material';

const SUSPENSION_REASONS = [
  { key: 'background_check_declined', label: 'Background Check Declined' },
  { key: 'attendance_reliability', label: 'Attendance & Reliability' },
  {
    key: 'qualification_eligibility',
    label: 'Qualification or Eligibility Issue',
  },
  { key: 'policy_enforcement', label: 'Policy Enforcement' },
  { key: 'safety_conduct', label: 'Safety or Professional Conduct' },
  { key: 'compliance_review', label: 'Compliance Issue' },
  { key: 'account_platform_use', label: 'Account or Platform Misuse' },
  { key: 'worker_request', label: 'Worker Request' },
] as const;

type Props = {
  workerId: number;
  workerName: string;
  onClose: () => void;
  onSuccess: () => void;
};

const PermanentSuspendModal = ({
  workerId,
  workerName,
  onClose,
  onSuccess,
}: Props) => {
  const [reason, setReason] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!reason) return;

    setLoading(true);
    setError(null);

    try {
      const response = await accountabilityAPI.suspendWorker(
        workerId,
        reason,
        internalNotes || undefined,
        endsAt || undefined,
      );

      if (response.success) {
        onSuccess();
        onClose();
      } else {
        setError(response.errors?.join(', ') || 'Failed to suspend worker');
      }
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to suspend worker';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      disableClickout
      size="sm"
      title="Suspend Worker's Account"
      onRequestClose={onClose}
    >
      <Card.Section>
        <Body>
          You are about to suspend <strong>{workerName}&apos;s</strong> account.
          Please select a reason to proceed.
        </Body>
      </Card.Section>
      <Card.Section>
        {error && (
          <Alert
            description={error}
            icon={faExclamationTriangle}
            status="danger"
            title="Error"
          />
        )}
        <Stack vertical gap={16}>
          <FormElement label="Reason">
            <Small color="lighter" style={{ marginBottom: 4 }}>
              Selected reason will be shown to GravyWorkers
            </Small>
            <FormControl fullWidth size="small">
              <InputLabel>Select reason</InputLabel>
              <Select
                label="Select reason"
                value={reason}
                onChange={(e: SelectChangeEvent) => setReason(e.target.value)}
              >
                {SUSPENSION_REASONS.map((r) => (
                  <MenuItem key={r.key} value={r.key}>
                    {r.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FormElement>
          <FormElement label="Notes">
            <Small color="lighter" style={{ marginBottom: 4 }}>
              Internal notes &ndash; visible to admins only
            </Small>
            <TextArea
              placeholder="Additional internal notes"
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
            />
          </FormElement>
          <FormElement label="Re-enable On (optional)">
            <Small color="warning" style={{ marginBottom: 4 }}>
              If no re-enable date is set, the user&apos;s account will be
              permanently disabled
            </Small>
            <TextField
              type="date"
              size="small"
              fullWidth
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              inputProps={{
                min: new Date(Date.now() + 86400000)
                  .toISOString()
                  .split('T')[0],
              }}
            />
          </FormElement>
        </Stack>
      </Card.Section>
      <Card.Section>
        <Stack justify="end">
          <Button
            a11yLabel="Cancel"
            appearance="outline"
            label="Cancel"
            onClick={onClose}
          />
          <Button
            a11yLabel="Confirm Suspension"
            isLoading={loading}
            label="Confirm Suspension"
            status="danger"
            onClick={handleSubmit}
            disabled={!reason}
          />
        </Stack>
      </Card.Section>
    </Modal>
  );
};

export default PermanentSuspendModal;
